// skipcq: JS-W1028
import { useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import BrewhousePageShell from '../../components/brewhouse/BrewhousePageShell';
import { UserContext } from '../../context/UserContext';
import { db } from '../../utils/firebase';
import {
  trackBrewhouseEmailPreferencesLoadError,
  trackBrewhouseEmailPreferencesSaveError,
} from '../../utils/brewhousePortalAnalytics';

function BreweryEmailPreferencesPage() {
  const user = useContext(UserContext);
  const [optedIn, setOptedIn] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (!user.isLoggedIn || typeof user.uid !== 'string') {
      setIsLoading(false);
      setPreferencesLoaded(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        if (cancelled) {
          return;
        }
        const preferences = userSnap.data()?.emailPreferences;
        setOptedIn(preferences?.breweryActivityEmails !== false);
        setPreferencesLoaded(true);
      } catch {
        if (!cancelled) {
          trackBrewhouseEmailPreferencesLoadError();
          setErrorMessage('Unable to load your email preferences.');
          setPreferencesLoaded(false);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user.isLoggedIn, user.uid]);

  const handleOptInChange = useCallback((event) => {
    setOptedIn(event.target.checked);
  }, []);

  const handleSave = useCallback(async () => {
    if (!user.isLoggedIn || typeof user.uid !== 'string' || !preferencesLoaded) {
      return;
    }
    setIsSaving(true);
    setMessage('');
    setErrorMessage('');
    try {
      await setDoc(
        doc(db, 'users', user.uid),
        {
          emailPreferences: {
            breweryActivityEmails: optedIn,
          },
        },
        { merge: true },
      );
      setMessage('Email preferences saved.');
    } catch {
      trackBrewhouseEmailPreferencesSaveError();
      setErrorMessage('Unable to save your email preferences.');
    } finally {
      setIsSaving(false);
    }
  }, [optedIn, preferencesLoaded, user.isLoggedIn, user.uid]);

  return (
    <BrewhousePageShell
      breadcrumbs={[{ label: 'Settings' }]}
      title="Email preferences"
      titleTestId="brewhouse-email-preferences-title"
      maxWidth={720}
    >
      <Stack spacing={2}>
        <Typography variant="body1" color="text.secondary">
          Manage monthly brewery activity emails from I&apos;d Tap That.
        </Typography>
        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
        {message ? <Alert severity="success">{message}</Alert> : null}
        <Paper sx={{ p: 3 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : preferencesLoaded ? (
            <Stack spacing={2}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={optedIn}
                    onChange={handleOptInChange}
                  />
                )}
                label="Receive monthly brewery activity emails"
              />
              <Button variant="contained" onClick={handleSave} disabled={isSaving}>
                Save preferences
              </Button>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Email preferences are unavailable right now. Please refresh the page and try again.
            </Typography>
          )}
        </Paper>
      </Stack>
    </BrewhousePageShell>
  );
}

export default BreweryEmailPreferencesPage;
